import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderAPI } from '../services/api';
import { useLang, TX } from '../context/LangContext';
import { toast } from 'react-toastify';
import './Cart.css';

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script'); s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true); s.onerror = () => resolve(false); document.body.appendChild(s);
});

const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Puducherry'];

export default function Cart() {
  const navigate = useNavigate();
  const { items, subtotal, gstAmount, shippingCharge, getTotal, removeFromCart, updateQty, clearCart } = useCart();
  const { isAuthenticated, user } = useAuth();
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(null);
  const [payMethod, setPayMethod] = useState('razorpay');
  const [loading, setLoading] = useState(false);
  const [addr, setAddr] = useState({ name:user?.name||'', phone:user?.phone||'', street:'', city:'', state:'Uttar Pradesh', pincode:'', country:'India' });
  const total = getTotal(discount);

  const applyCoupon = async () => {
    try {
      const res = await orderAPI.validateCoupon({ code: couponCode, orderAmount: subtotal });
      setDiscount(res.discount); setCouponApplied(res.coupon);
      toast.success(`🎉 ₹${res.discount} discount applied!`);
    } catch (err) { toast.error(err.message); }
  };

  if (!items.length && step === 1) return (
    <div className="cart-page"><div className="container"><div className="empty-state" style={{ padding:'5rem 1rem' }}>
      <span className="empty-icon">🛒</span>
      <h2 className="font-devanagari" style={{ color:'var(--maroon)' }}>{t(TX.emptyCart)}</h2>
      <button className="btn btn-primary" style={{ marginTop:'1.5rem' }} onClick={() => navigate('/shop')}>🛍️ {t(TX.shopNow)}</button>
    </div></div></div>
  );

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      const res = await orderAPI.create({ items: items.map(i => ({ product:i._id, quantity:i.qty })), shippingAddress:addr, paymentMethod:payMethod, couponCode: couponApplied?.code });
      if (payMethod === 'razorpay') {
        await loadRazorpay();
        new window.Razorpay({
          key: res.razorpayKeyId, amount: res.razorpayOrder.amount, currency:'INR',
          name:'राघवेंद्र पूजन भंडार', order_id: res.razorpayOrder.id,
          handler: async (rp) => {
            await orderAPI.verifyPayment({ ...rp, razorpayOrderId:rp.razorpay_order_id, razorpayPaymentId:rp.razorpay_payment_id, razorpaySignature:rp.razorpay_signature, orderId:res.order._id });
            clearCart(); toast.success('🎉 Order placed! जय श्री राम 🙏'); navigate(`/order-success/${res.order._id}`);
          },
          prefill:{ name:user?.name, email:user?.email, contact:addr.phone },
          theme:{ color:'#7B1427' }, modal:{ ondismiss:() => toast.info('Payment cancelled') }
        }).open();
      } else { clearCart(); navigate(`/order-success/${res.order._id}`); }
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="cart-page">
      <div className="container">
        {/* Steps */}
        <div className="cart-steps">
          {[{n:1,hi:'टोकरी',en:'Cart'},{n:2,hi:'पता',en:'Address'},{n:3,hi:'भुगतान',en:'Payment'}].map(s => (
            <div key={s.n} className={`cart-step${step>=s.n?' done':''}${step===s.n?' active':''}`}>
              <div className="step-circle">{step>s.n?'✓':s.n}</div>
              <span className="step-label font-devanagari">{t(s)}</span>
              {s.n<3&&<div className={`step-line${step>s.n?' done':''}`}/>}
            </div>
          ))}
        </div>

        {step===1 && (
          <div className="cart-layout">
            <div className="cart-items-col">
              <h2 className="cart-col-title font-devanagari">🛒 {t({ hi:'आपकी टोकरी', en:'Your Cart' })} ({items.length})</h2>
              {items.map(item => (
                <div key={item._id} className="cart-item">
                  <div className="cart-item-img">{item.image?<img src={item.image} alt={item.nameEn}/>:'🛕'}</div>
                  <div className="cart-item-body">
                    <p className="cart-item-name font-devanagari">{item.nameHi}</p>
                    <p className="cart-item-name-en">{item.nameEn}</p>
                    <div className="cart-item-footer">
                      <div className="qty-controls">
                        <button className="qty-btn" onClick={() => updateQty(item._id, item.qty-1, item.selectedVariant)}><Minus size={12}/></button>
                        <span className="qty-val">{item.qty}</span>
                        <button className="qty-btn" onClick={() => updateQty(item._id, item.qty+1, item.selectedVariant)}><Plus size={12}/></button>
                      </div>
                      <span className="cart-item-price">₹{(item.price*item.qty).toLocaleString('en-IN')}</span>
                      <button className="cart-item-remove" onClick={() => removeFromCart(item._id, item.selectedVariant)}><Trash2 size={14}/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-summary-col">
              <div className="coupon-box">
                <h3 className="coupon-title"><Tag size={14}/> Coupon</h3>
                {couponApplied ? <div className="coupon-applied"><span>🎉 {couponApplied.code} — ₹{discount} off</span><button onClick={() => {setCouponApplied(null);setDiscount(0);}}><Trash2 size={13}/></button></div>
                : <div className="coupon-input-row"><input className="form-input" placeholder="WELCOME10..." value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}/><button className="btn btn-maroon btn-sm" onClick={applyCoupon}>Apply</button></div>}
              </div>
              <div className="order-summary">
                <h3 className="summary-title font-devanagari">📋 {t({ hi:'ऑर्डर सारांश', en:'Order Summary' })}</h3>
                <div className="summary-row"><span>{t(TX.subtotal)}</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
                <div className="summary-row"><span>{t(TX.gst)}</span><span>₹{gstAmount.toLocaleString('en-IN')}</span></div>
                <div className="summary-row"><span>{t(TX.shipping)}</span><span className={shippingCharge===0?'free-label':''}>{shippingCharge===0?`🎁 ${t(TX.free)}`:`₹${shippingCharge}`}</span></div>
                {discount>0&&<div className="summary-row discount-row"><span>Discount</span><span>-₹{discount}</span></div>}
                <div className="summary-row summary-total"><span>{t(TX.total)}</span><span>₹{total.toLocaleString('en-IN')}</span></div>
              </div>
              <button className="btn btn-primary btn-full" onClick={() => { if(!isAuthenticated){toast.info('🔐 Please login first');navigate('/login',{state:{from:'/cart'}});return;}setStep(2);}}>
                {t({ hi:'आगे बढ़ें', en:'Continue' })} <ChevronRight size={15}/>
              </button>
            </div>
          </div>
        )}

        {step===2 && (
          <div className="address-step">
            <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
            <h2 className="cart-col-title font-devanagari">📍 {t({ hi:'डिलीवरी पता', en:'Delivery Address' })}</h2>
            <form className="address-form" onSubmit={e => { e.preventDefault(); setStep(3); }}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Name *</label><input className="form-input" value={addr.name} onChange={e => setAddr({...addr,name:e.target.value})} required/></div>
                <div className="form-group"><label className="form-label">Phone *</label><input className="form-input" value={addr.phone} onChange={e => setAddr({...addr,phone:e.target.value})} required/></div>
              </div>
              <div className="form-group"><label className="form-label">Street *</label><input className="form-input" value={addr.street} onChange={e => setAddr({...addr,street:e.target.value})} required placeholder="House no, Colony..."/></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">City *</label><input className="form-input" value={addr.city} onChange={e => setAddr({...addr,city:e.target.value})} required/></div>
                <div className="form-group"><label className="form-label">Pincode *</label><input className="form-input" value={addr.pincode} onChange={e => setAddr({...addr,pincode:e.target.value})} maxLength={6} required/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">State *</label><select className="form-input" value={addr.state} onChange={e => setAddr({...addr,state:e.target.value})}>{STATES.map(s=><option key={s}>{s}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Country</label><select className="form-input" value={addr.country} onChange={e => setAddr({...addr,country:e.target.value})}>{['India','USA','UK','Canada','Australia','UAE','Singapore','Other'].map(c=><option key={c}>{c}</option>)}</select></div>
              </div>
              <button type="submit" className="btn btn-primary btn-full">Continue to Payment <ChevronRight size={15}/></button>
            </form>
          </div>
        )}

        {step===3 && (
          <div className="payment-step">
            <button className="btn btn-ghost" onClick={() => setStep(2)}>← Back</button>
            <div className="payment-layout">
              <div>
                <h2 className="cart-col-title font-devanagari">💳 Payment</h2>
                <div className="payment-methods">
                  {[{id:'razorpay',label:'💳 Razorpay',desc:'UPI · Card · Net Banking',rec:true},{id:'cod',label:'💵 Cash on Delivery',desc:'+ ₹40 COD charge'}].map(pm => (
                    <div key={pm.id} className={`pay-option${payMethod===pm.id?' selected':''}`} onClick={() => setPayMethod(pm.id)}>
                      <div className="pay-radio"><div className={`pay-radio-dot${payMethod===pm.id?' on':''}`}/></div>
                      <div><div className="pay-name">{pm.label}</div><div className="pay-desc">{pm.desc}</div></div>
                      {pm.rec&&<div className="pay-badge-recommended">Recommended</div>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="order-summary">
                  <h3 className="summary-title font-devanagari">📦 Order Summary</h3>
                  <div className="summary-row"><span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
                  <div className="summary-row"><span>GST</span><span>₹{gstAmount.toLocaleString('en-IN')}</span></div>
                  <div className="summary-row"><span>Shipping</span><span className={shippingCharge===0?'free-label':''}>{shippingCharge===0?'🎁 FREE':`₹${shippingCharge}`}</span></div>
                  {payMethod==='cod'&&<div className="summary-row"><span>COD</span><span>₹40</span></div>}
                  {discount>0&&<div className="summary-row discount-row"><span>Discount</span><span>-₹{discount}</span></div>}
                  <div className="summary-row summary-total"><span>Total</span><span>₹{(total+(payMethod==='cod'?40:0)).toLocaleString('en-IN')}</span></div>
                </div>
                <button className="btn btn-primary btn-full" style={{ marginTop:'1rem' }} onClick={handlePlaceOrder} disabled={loading}>
                  {loading?<span className="auth-spinner"/>:`🛕 ${t(TX.placeOrder)} — ${t(TX.jayShriRam)}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
